/**
 * Image utility functions for PDF generation with pdfmake
 */

/**
 * Convert an image URL to base64 data URL
 * pdfmake requires images in base64 format
 */
export const getBase64Image = async (url: string): Promise<string> => {
  try {
    const response = await fetch(url, {
      mode: 'cors',
      credentials: 'same-origin',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }

    const blob = await response.blob();

    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to convert image to base64'));
        }
      };
      reader.onerror = () => {
        reject(new Error('FileReader error'));
      };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    // Return a placeholder or empty string if conversion fails
    return '';
  }
};

/**
 * Cache for converted base64 images
 * Prevents redundant conversions of the same image
 */
const imageCache = new Map<string, string>();

/**
 * Get base64 image with caching
 */
export const getCachedBase64Image = async (url: string): Promise<string> => {
  const cached = imageCache.get(url);
  if (cached) {
    return cached;
  }

  const base64 = await getBase64Image(url);

  if (base64) {
    imageCache.set(url, base64);
  }

  return base64;
};

/**
 * Clear the image cache
 */
export const clearImageCache = (): void => {
  imageCache.clear();
};
